import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { BRAND } from "../lib/brand";
import { mulberry32, LAKES, getBenches, doorGeom, APRON } from "./helpers";

const OUTFIT = [BRAND.charcoal, BRAND.yellow, BRAND.white, "#6E6E68", "#3A3E46", "#8FA877", "#B5651D", "#A23B3B", "#4C6B8A", "#7D5BA6"];
const HEAD = ["#C9A98B", "#E0B999", "#A87B5A", "#8D5A3C"];
const WALK_RADIUS = 32;
const N = 50;
const DOOR_W = 0.92;
const DOOR_H = 1.55;
const ZONE = 1.35;   // distance at which a door opens for its visitor

// Humorous office personas randomly assigned to citizens — clicking a person opens
// the InfoPanel with one of these little bios. Kept light, brand-friendly and silly.
const PERSONAS = [
  { name: "Dave from Accounts", role: "Spreadsheet Whisperer", bio: "Has opinions about pivot tables that no one asked for. Currently on his fourth coffee and his first actual task of the day. Believes 'reply all' is a personality." },
  { name: "Priya, Creative Lead", role: "Idea Tornado", bio: "Pitches eleven brilliant concepts before lunch and forgets ten of them by 2pm. Communicates exclusively in mood boards and dramatic sighs." },
  { name: "Big Steve", role: "Logistics & Snacks", bio: "Knows where the good biscuits are hidden and guards that secret with his life. Has reverse-parked the same van for nine years." },
  { name: "Nina", role: "Junior Strategist", bio: "Said 'let's circle back' so many times the office started spinning. Still genuinely excited about everything, which is honestly lovely." },
  { name: "Gareth", role: "IT, Reluctantly", bio: "Will fix your laptop but make you feel slightly bad about it first. Has turned it off and on again more times than he's blinked today." },
  { name: "Margot", role: "Brand Guardian", bio: "Can spot a non-brand-approved shade of yellow from across a car park. Once cried at a beautifully kerned headline. Worth it." },
  { name: "Tariq", role: "Account Manager", bio: "Smiles through every client call while quietly screaming inside. Owns seventeen near-identical navy jumpers and regrets none of them." },
  { name: "Lou", role: "Social Media Goblin", bio: "Hasn't slept since the last trending sound. Speaks fluent meme and broken English. Will absolutely subtweet this building." },
  { name: "Derek", role: "Senior Consultant", bio: "Uses the word 'synergy' unironically and charges by the hour. His slides have slides. Nobody knows what he actually does, including Derek." },
  { name: "Aisha", role: "Designer", bio: "Just needs 'five more minutes' on the logo for the third day running. Has 412 unsaved tabs open and a deep, abiding love for grids." },
  { name: "Colin", role: "Facilities", bio: "The thermostat is a lie and Colin is the only one who knows the truth. Fiercely protective of the good stapler." },
  { name: "Bex", role: "Events Producer", bio: "Can build a pop-up stand from gaffer tape and pure willpower. Has a clipboard and isn't afraid to use it. Lunch is for the weak." },
  { name: "Hari, the Intern", role: "Professional Volunteer", bio: "Said yes to a task three weeks ago and is still finding out what it was. Makes a genuinely elite cup of tea, to be fair." },
  { name: "Sandra", role: "Office Manager", bio: "Runs this entire place and lets the directors think they do. Knows everyone's birthday, allergies, and minor secrets." },
  { name: "Mike", role: "Sales", bio: "Would sell you the bench you're sitting on. Laughs a beat too long at every joke. His handshake has its own gym membership." },
  { name: "Jo", role: "Copywriter", bio: "Spent forty minutes deciding between a comma and a dash. Chose the dash. Still thinks about the comma. Haunted, frankly." },
  { name: "Wandering Greg", role: "Status: Unknown", bio: "Nobody is entirely sure which department Greg is in, including HR. Always walking somewhere with great purpose. Beloved regardless." },
  { name: "Fatima", role: "Data Analyst", bio: "Turns chaos into charts and charts into mild panic for management. Has a graph that proves she needs a holiday. It's very convincing." },
];

// Build the InfoPanel-shaped object for a clicked citizen (brand colours so the
// panel's icon + accent stay on-brand).
function makePersona(ag) {
  const p = PERSONAS[ag.personaIdx % PERSONAS.length];
  return {
    slug: "citizen",
    name: p.name,
    category: p.role,
    description: p.bio,
    color: BRAND.charcoal,
    accent: BRAND.yellow,
    isPerson: true,
  };
}

// Door placement/orientation is computed from the building's rotation-aware front
// (see doorGeom in helpers.js) so citizens always approach the true entrance.
function doorSpec(b, bi) {
  const g = doorGeom(b);
  return {
    bx: g.bx, bz: g.bz, nx: g.nx, nz: g.nz,
    ax: g.dx, az: g.dz,
    rotY: g.rotY,
    revolving: b.shape === "hq",
    color: b.color || BRAND.charcoal,
    accent: b.accent || BRAND.yellow,
    frame: (b.color || "").toUpperCase() === "#FFCE24" ? BRAND.charcoal : (b.accent || BRAND.yellow),
    bi,
  };
}

function pointBlocked(x, z, obstacles) {
  if (x * x + z * z > WALK_RADIUS * WALK_RADIUS) return true;
  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i];
    const dx = o.x - x;
    const dz = o.z - z;
    if (dx * dx + dz * dz < o.r * o.r) return true;
  }
  return false;
}

function segClear(x0, z0, x1, z1, obstacles) {
  const dist = Math.hypot(x1 - x0, z1 - z0);
  const steps = Math.max(2, Math.ceil(dist / 0.7));
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    if (pointBlocked(x0 + (x1 - x0) * t, z0 + (z1 - z0) * t, obstacles)) return false;
  }
  return true;
}

function HingedDoor({ leftRef, rightRef, frame, accent }) {
  return (
    <group>
      <mesh position={[0, DOOR_H / 2, -0.03]}>
        <planeGeometry args={[DOOR_W + 0.2, DOOR_H + 0.14]} />
        <meshStandardMaterial color={"#1d1f24"} roughness={0.9} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
      </mesh>
      <mesh position={[-(DOOR_W / 2 + 0.07), DOOR_H / 2, 0.02]} castShadow><boxGeometry args={[0.1, DOOR_H + 0.18, 0.16]} /><meshStandardMaterial color={frame} roughness={0.5} metalness={0.2} /></mesh>
      <mesh position={[DOOR_W / 2 + 0.07, DOOR_H / 2, 0.02]} castShadow><boxGeometry args={[0.1, DOOR_H + 0.18, 0.16]} /><meshStandardMaterial color={frame} roughness={0.5} metalness={0.2} /></mesh>
      <mesh position={[0, DOOR_H + 0.11, 0.02]} castShadow><boxGeometry args={[DOOR_W + 0.28, 0.12, 0.16]} /><meshStandardMaterial color={frame} roughness={0.5} metalness={0.2} /></mesh>
      <mesh position={[0, 0.04, 0.18]}><boxGeometry args={[DOOR_W + 0.34, 0.08, 0.42]} /><meshStandardMaterial color={"#9A968C"} roughness={0.9} /></mesh>
      <group ref={leftRef} position={[-(DOOR_W / 2), 0, 0.07]}>
        <mesh position={[DOOR_W / 4, DOOR_H / 2, 0]} castShadow><boxGeometry args={[DOOR_W / 2, DOOR_H, 0.06]} /><meshStandardMaterial color={accent} roughness={0.45} metalness={0.2} /></mesh>
        <mesh position={[DOOR_W / 4, DOOR_H * 0.62, 0.04]}><planeGeometry args={[DOOR_W * 0.3, DOOR_H * 0.42]} /><meshStandardMaterial color={"#bfe3ef"} transparent opacity={0.6} roughness={0.1} metalness={0.3} /></mesh>
      </group>
      <group ref={rightRef} position={[DOOR_W / 2, 0, 0.07]}>
        <mesh position={[-DOOR_W / 4, DOOR_H / 2, 0]} castShadow><boxGeometry args={[DOOR_W / 2, DOOR_H, 0.06]} /><meshStandardMaterial color={accent} roughness={0.45} metalness={0.2} /></mesh>
        <mesh position={[-DOOR_W / 4, DOOR_H * 0.62, 0.04]}><planeGeometry args={[DOOR_W * 0.3, DOOR_H * 0.42]} /><meshStandardMaterial color={"#bfe3ef"} transparent opacity={0.6} roughness={0.1} metalness={0.3} /></mesh>
      </group>
    </group>
  );
}

function RevolvingDoor({ revRef, color, accent }) {
  const R = 0.64;                 // glass drum radius
  const SPAN = R * 2 - 0.06;      // a single wing spans (almost) the full diameter
  const PANEL_H = DOOR_H * 0.94;
  return (
    <group position={[0, 0, 0.18]}>
      {/* glass drum enclosure */}
      <mesh position={[0, DOOR_H / 2, 0]}>
        <cylinderGeometry args={[R, R, DOOR_H, 24, 1, true]} />
        <meshStandardMaterial color={"#bfe3ef"} transparent opacity={0.16} side={THREE.DoubleSide} roughness={0.1} metalness={0.3} />
      </mesh>
      {/* two full-width panels crossing at the centre (a "+" from above) */}
      <group ref={revRef} position={[0, DOOR_H / 2, 0]}>
        {[0, Math.PI / 2].map((rot, i) => (
          <group key={i} rotation={[0, rot, 0]}>
            {/* the glass leaf */}
            <mesh castShadow>
              <boxGeometry args={[SPAN, PANEL_H, 0.05]} />
              <meshStandardMaterial color={"#9fd0e0"} transparent opacity={0.55} side={THREE.DoubleSide} roughness={0.1} metalness={0.3} />
            </mesh>
            {/* accent rubber edges at both tips of the leaf */}
            <mesh position={[SPAN / 2, 0, 0]}><boxGeometry args={[0.06, PANEL_H, 0.09]} /><meshStandardMaterial color={accent} metalness={0.4} roughness={0.3} /></mesh>
            <mesh position={[-SPAN / 2, 0, 0]}><boxGeometry args={[0.06, PANEL_H, 0.09]} /><meshStandardMaterial color={accent} metalness={0.4} roughness={0.3} /></mesh>
          </group>
        ))}
        {/* central spindle */}
        <mesh><cylinderGeometry args={[0.06, 0.06, DOOR_H * 0.98, 8]} /><meshStandardMaterial color={accent} metalness={0.4} roughness={0.3} /></mesh>
      </group>
      {/* drum cap */}
      <mesh position={[0, DOOR_H + 0.07, 0]} castShadow><cylinderGeometry args={[R + 0.08, R + 0.08, 0.13, 24]} /><meshStandardMaterial color={color} roughness={0.5} metalness={0.15} /></mesh>
    </group>
  );
}

export default function Life({ buildings, onPersonSelect }) {
  const setup = useMemo(() => {
    const list = buildings || [];
    const doors = list.map((b, i) => doorSpec(b, i));

    const obstacles = list.map((b) => {
      const w = Number(b.width) || 4;
      const d = Number(b.depth) || 4;
      return { x: Number(b.positionX) || 0, z: Number(b.positionZ) || 0, r: Math.max(w, d) * 0.5 + 1.8 };
    });
    LAKES.forEach((l) => obstacles.push({ x: l.x, z: l.z, r: l.r + 0.8 }));
    obstacles.push({ x: 9, z: 9, r: 3.6 }); // fountain
    obstacles.push({ x: -29, z: -29, r: 3.8 }); // owl shrine

    const benches = getBenches(buildings);
    const rng = mulberry32(98765);

    // scatter citizen start points in open space
    const starts = [];
    let guard = 0;
    while (starts.length < N && guard < N * 60) {
      guard++;
      const r = 3 + Math.sqrt(rng()) * (WALK_RADIUS - 4);
      const a = rng() * Math.PI * 2;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      if (!pointBlocked(x, z, obstacles)) starts.push({ x, z });
    }
    return { doors, obstacles, benches, starts, rng };
  }, [buildings]);

  const { doors, obstacles, benches } = setup;

  const doorNodes = useRef(doors.map(() => ({ left: React.createRef(), right: React.createRef(), rev: React.createRef(), open: 0, revSpin: 0, revVel: 0 })));
  const citNodes = useRef(setup.starts.map(() => ({ group: React.createRef(), bob: React.createRef(), cap: React.createRef(), head: React.createRef() })));
  const rngRef = useRef(mulberry32(424242));

  const agents = useRef(
    setup.starts.map((s, i) => {
      const inside = i % 4 === 0 && doors.length > 0;
      return {
        state: inside ? "inside" : "roam",
        timer: inside ? 0.4 + rngRef.current() * 3 : 0,
        pos: { x: s.x, z: s.z },
        tgt: { x: s.x, z: s.z },
        heading: rngRef.current() * Math.PI * 2,
        speed: 1.0 + rngRef.current() * 1.1,
        doorIndex: inside ? Math.floor(rngRef.current() * doors.length) : -1,
        sitRot: 0,
        benchIndex: -1,
        benchSlot: -1,
        scale: inside ? 0 : 1,
        outfit: OUTFIT[i % OUTFIT.length],
        head: HEAD[i % HEAD.length],
        personaIdx: Math.floor(rngRef.current() * PERSONAS.length),
      };
    })
  );

  const r = () => rngRef.current();

  // Bench seating: each bench has 3 side-by-side seats. We track which are taken so
  // people sit next to each other instead of stacking on the same spot.
  const SEAT_SLOTS = [-0.46, 0, 0.46];
  const benchOcc = useRef(benches.map(() => [false, false, false]));
  const releaseSeat = (ag) => {
    if (ag.benchIndex >= 0 && ag.benchSlot >= 0 && benchOcc.current[ag.benchIndex]) {
      benchOcc.current[ag.benchIndex][ag.benchSlot] = false;
    }
    ag.benchIndex = -1;
    ag.benchSlot = -1;
  };

  const recolor = (i, ag) => {
    ag.outfit = OUTFIT[Math.floor(r() * OUTFIT.length)];
    ag.head = HEAD[Math.floor(r() * HEAD.length)];
    ag.personaIdx = Math.floor(r() * PERSONAS.length); // a "fresh" person gets a fresh bio
    const cap = citNodes.current[i].cap.current;
    const hd = citNodes.current[i].head.current;
    if (cap && cap.material) cap.material.color.set(ag.outfit);
    if (hd && hd.material) hd.material.color.set(ag.head);
  };

  const pickRoam = (ag) => {
    for (let k = 0; k < 16; k++) {
      const a = r() * Math.PI * 2;
      const rad = 4 + r() * 13;
      const nx = ag.pos.x + Math.cos(a) * rad;
      const nz = ag.pos.z + Math.sin(a) * rad;
      if (!pointBlocked(nx, nz, obstacles) && segClear(ag.pos.x, ag.pos.z, nx, nz, obstacles)) {
        ag.tgt = { x: nx, z: nz };
        ag.state = "roam";
        return;
      }
    }
    ag.tgt = { x: ag.pos.x * 0.8, z: ag.pos.z * 0.8 };
    ag.state = "roam";
  };

  const decide = (ag) => {
    const roll = r();
    // ~30%: head to a building door (must be reachable -> emergent "walks up and enters")
    if (doors.length && roll < 0.3) {
      for (let k = 0; k < 5; k++) {
        const j = Math.floor(r() * doors.length);
        const dr = doors[j];
        const apx = dr.ax + dr.nx * APRON;
        const apz = dr.az + dr.nz * APRON;
        if (!pointBlocked(apx, apz, obstacles) && segClear(ag.pos.x, ag.pos.z, apx, apz, obstacles)) {
          ag.doorIndex = j;
          ag.tgt = { x: apx, z: apz };
          ag.state = "toApron";
          return;
        }
      }
    }
    // ~22%: go sit on a free seat of a bench (people sit side-by-side, not stacked)
    if (benches.length && roll < 0.52) {
      for (let k = 0; k < 6; k++) {
        const bi = Math.floor(r() * benches.length);
        const bch = benches[bi];
        const occ = benchOcc.current[bi];
        const order = r() < 0.5 ? [0, 1, 2] : [2, 1, 0];
        let slot = -1;
        for (let o = 0; o < order.length; o++) { if (!occ[order[o]]) { slot = order[o]; break; } }
        if (slot < 0) continue; // bench full
        const a = bch.a || 0;
        const slotX = SEAT_SLOTS[slot];
        const sx = bch.x + Math.cos(a) * slotX;
        const sz = bch.z - Math.sin(a) * slotX;
        const dd = Math.hypot(sx - ag.pos.x, sz - ag.pos.z);
        if (dd > 1.2 && dd < 22 && segClear(ag.pos.x, ag.pos.z, sx, sz, obstacles)) {
          occ[slot] = true;
          ag.benchIndex = bi;
          ag.benchSlot = slot;
          ag.tgt = { x: sx, z: sz };
          ag.sitRot = a;
          ag.state = "toSit";
          return;
        }
      }
    }
    pickRoam(ag);
  };

  const moveToward = (ag, dt) => {
    const dx = ag.tgt.x - ag.pos.x;
    const dz = ag.tgt.z - ag.pos.z;
    const dist = Math.hypot(dx, dz);
    if (dist > 0.0001) {
      const step = Math.min(dist, ag.speed * dt);
      ag.pos.x += (dx / dist) * step;
      ag.pos.z += (dz / dist) * step;
      const th = Math.atan2(dx, dz);
      let diff = th - ag.heading;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      ag.heading += diff * Math.min(1, dt * 8);
    }
    return dist;
  };

  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.05);
    const time = state.clock.elapsedTime;
    const demand = new Array(doors.length).fill(false);

    for (let i = 0; i < agents.current.length; i++) {
      const ag = agents.current[i];
      ag.timer -= dt;
      const dr = ag.doorIndex >= 0 ? doors[ag.doorIndex] : null;
      let walking = false;
      let lowered = 0;

      switch (ag.state) {
        case "roam": {
          const d = moveToward(ag, dt);
          walking = d > 0.4;
          if (d < 0.4) {
            ag.timer -= dt;
            if (ag.timer <= 0) {
              decide(ag);
              ag.timer = 0.2 + r() * 1.0;
            }
          }
          break;
        }
        case "toApron": {
          const d = moveToward(ag, dt);
          walking = true;
          if (d < 0.35 && dr) {
            ag.tgt = { x: dr.ax, z: dr.az };
            ag.state = "toDoor";
          }
          break;
        }
        case "toDoor": {
          const d = moveToward(ag, dt);
          walking = true;
          if (d < 0.3) {
            ag.state = "entering";
            ag.timer = 0.5;
          }
          break;
        }
        case "entering": {
          if (dr) {
            ag.tgt = { x: dr.ax - dr.nx * 0.5, z: dr.az - dr.nz * 0.5 };
            moveToward(ag, dt);
          }
          ag.scale += (0 - ag.scale) * Math.min(1, dt * 6);
          walking = true;
          if (ag.timer <= 0) {
            ag.state = "inside";
            ag.timer = 2.5 + r() * 5.5;
            ag.scale = 0;
          }
          break;
        }
        case "inside": {
          if (ag.timer <= 0 && doors.length) {
            // re-emerge from a (possibly different) door as a fresh-looking person
            const j = Math.floor(r() * doors.length);
            const ex = doors[j];
            ag.doorIndex = j;
            ag.pos = { x: ex.ax, z: ex.az };
            ag.tgt = { x: ex.ax + ex.nx * (APRON + r() * 1.5), z: ex.az + ex.nz * (APRON + r() * 1.5) };
            ag.state = "exiting";
            ag.scale = 0;
            recolor(i, ag);
          }
          break;
        }
        case "exiting": {
          const d = moveToward(ag, dt);
          ag.scale += (1 - ag.scale) * Math.min(1, dt * 6);
          walking = true;
          if (d < 0.35) {
            ag.scale = 1;
            ag.state = "roam";
            ag.doorIndex = -1;
          }
          break;
        }
        case "toSit": {
          const d = moveToward(ag, dt);
          walking = true;
          if (d < 0.35) {
            ag.pos = { x: ag.tgt.x, z: ag.tgt.z };
            ag.state = "sit";
            ag.timer = 3 + r() * 1.2;
          }
          break;
        }
        case "sit": {
          lowered = -0.12;
          ag.heading = ag.sitRot;
          if (ag.timer <= 0) { releaseSeat(ag); decide(ag); }
          break;
        }
        default:
          break;
      }

      // door demand
      if (dr) {
        if (ag.state === "entering" || ag.state === "exiting") demand[ag.doorIndex] = true;
        else if (ag.state === "toDoor" && Math.hypot(dr.ax - ag.pos.x, dr.az - ag.pos.z) < ZONE) demand[ag.doorIndex] = true;
      }

      const node = citNodes.current[i];
      const g = node.group.current;
      if (g) {
        g.position.set(ag.pos.x, lowered, ag.pos.z);
        g.rotation.y = ag.heading;
        const s = ag.state === "inside" ? 0 : ag.scale;
        g.scale.setScalar(s);
        g.visible = s > 0.02;
      }
      if (node.bob.current) node.bob.current.position.y = walking ? Math.abs(Math.sin(time * 7 + i * 2)) * 0.06 : 0;
    }

    // apply door open + revolving
    for (let j = 0; j < doors.length; j++) {
      const dn = doorNodes.current[j];
      const tgt = demand[j] ? 1 : 0;
      dn.open += (tgt - dn.open) * Math.min(1, dt * 6);
      if (dn.left.current) dn.left.current.rotation.y = -dn.open * 1.4;
      if (dn.right.current) dn.right.current.rotation.y = dn.open * 1.4;
      // Revolving door only turns while a visitor is going in / coming out;
      // it spins up on demand and gently coasts to a stop afterwards.
      const wantSpin = demand[j] ? 1.7 : 0;
      dn.revVel += (wantSpin - dn.revVel) * Math.min(1, dt * 3);
      dn.revSpin += dn.revVel * dt;
      if (dn.rev.current) dn.rev.current.rotation.y = dn.revSpin;
    }
  });

  return (
    <group>
      {/* doors */}
      {doors.map((dr, j) => (
        <group key={`door${j}`} position={[dr.ax, 0, dr.az]} rotation={[0, dr.rotY, 0]}>
          {dr.revolving ? (
            <RevolvingDoor revRef={doorNodes.current[j].rev} color={dr.color} accent={dr.accent} />
          ) : (
            <HingedDoor leftRef={doorNodes.current[j].left} rightRef={doorNodes.current[j].right} frame={dr.frame} accent={dr.accent} />
          )}
        </group>
      ))}

      {/* citizens */}
      <group position={[0, 0.42, 0]}>
        {citNodes.current.map((node, i) => (
          <group key={`cit${i}`} ref={node.group}>
            {/* invisible click target — opens this person's humorous persona panel.
                Sits inside the citizen group so it scales/hides exactly with them
                (no clicks while they're "inside" a building, scale → 0, visible off). */}
            <mesh
              position={[0, 0.32, 0]}
              onClick={(e) => {
                e.stopPropagation();
                if (onPersonSelect) onPersonSelect(makePersona(agents.current[i]));
              }}
              onPointerOver={(e) => { e.stopPropagation(); if (typeof document !== "undefined") document.body.style.cursor = "pointer"; }}
              onPointerOut={() => { if (typeof document !== "undefined") document.body.style.cursor = "auto"; }}
            >
              <capsuleGeometry args={[0.55, 0.9, 4, 8]} />
              <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>
            <group ref={node.bob}>
              <mesh ref={node.cap} castShadow>
                <capsuleGeometry args={[0.16, 0.5, 4, 8]} />
                <meshStandardMaterial color={agents.current[i].outfit} roughness={0.7} flatShading />
              </mesh>
              <mesh ref={node.head} position={[0, 0.5, 0]} castShadow>
                <sphereGeometry args={[0.16, 10, 10]} />
                <meshStandardMaterial color={agents.current[i].head} roughness={0.6} />
              </mesh>
            </group>
          </group>
        ))}
      </group>
    </group>
  );
}
